import { useStore } from './useStore';

import type { TimerSlice } from './slices/timer/types';
import {
  GeolocationContextPayload,
  GeolocationErrorPayload,
  GeolocationStatusPayload,
  GeolocationStoreParts,
} from '@/store/slices/geolocation/types';
import {
  CustomerDataErrorPayload,
  CustomerDataPayload,
  CustomerPreferencesPayload,
  AmplitudeExperimentsPayload,
  CustomerStoreParts,
} from './slices/customerData/types';
import {
  ClientStatusPayload,
  GameContextPayload,
  GameErrorPayload,
  GamePayloadPayload,
  GameStatusPayload,
  GameStoreParts,
  ResetAllGamePartsOptions,
} from './slices/game/types';

import {
  RecentlyPlayedContextPayload,
  RecentlyPlayedErrorPayload,
  RecentlyPlayedStatusPayload,
  RecentlyPlayedStoreParts,
} from './slices/recentlyPlayed/types';
import {
  UserContextPayload,
  UserStatusPayload,
  UserErrorPayload,
} from './slices/userContext/types';
import { ThemeSlice } from './slices/theme/types';
import {
  UserSessionContextPayload,
  UserSessionErrorPayload,
  UserSessionStoreParts,
} from './slices/session/types';
import { GameWindowSlice } from './slices/gameWindow/types';
import { CommonUIErrorPayload, CommonUIPayload } from './slices/commonUi/types';
import { PopupTypes } from '@/features/Popup/types.ts';
import { ErrorPayload, ErrorType } from '@/utilities/errors/types';
import { ShowoffSlice } from './slices/showOff/showoff';
import { SwjSliceProps } from './slices/swj/swj';
import { DeviceOrientationInfo } from './slices/deviceSafeZone/deviceSafeZone';

const resetPartOrAll = <T extends string, O>(
  part: T | undefined,
  resetPart: (p: T, options?: O) => void,
  resetAll: (options?: O) => void,
  options?: O,
) => {
  if (part) {
    return resetPart(part, options);
  }
  return resetAll(options);
};

export const setGameContext = (payload: GameContextPayload) => {
  useStore.getState().setGameContext(payload);
};

export const setGameStatus = (payload: GameStatusPayload) =>
  useStore.getState().setGameStatus(payload);

export const setClientStatus = (payload: ClientStatusPayload) =>
  useStore.getState().setClientStatus(payload);

export const setGamePayload = (payload: GamePayloadPayload) =>
  useStore.getState().setGamePayload(payload);

export const setGameError = (payload: GameErrorPayload) =>
  useStore.getState().setGameError(payload);

export const resetGameStore = (
  part?: GameStoreParts,
  options?: ResetAllGamePartsOptions,
) => {
  const { resetGamePart, resetAllGameParts } = useStore.getState();
  return resetPartOrAll(part, resetGamePart, resetAllGameParts, options);
};

export type { ResetAllGamePartsOptions };

export const setUserSessionContext = (payload: UserSessionContextPayload) =>
  useStore.getState().setUserSessionContext(payload);
export const setUserSessionError = (payload: UserSessionErrorPayload) =>
  useStore.getState().setUserSessionError(payload);

export const resetUserSessionStore = (part?: UserSessionStoreParts) => {
  const { resetUserSessionPart, resetAllUserSessionParts } =
    useStore.getState();
  return resetPartOrAll(part, resetUserSessionPart, resetAllUserSessionParts);
};

export const setCommonUIContext = (payload: CommonUIPayload) =>
  useStore.getState().setCommonUIContext(payload);

export const setCommonUIError = (payload: CommonUIErrorPayload) =>
  useStore.getState().setCommonUIError(payload);

export const resetCommonUIStore = () =>
  useStore.getState().resetCommonUIContext();

export const setGameWindowContext = (
  payload: Partial<GameWindowSlice['gameWindowContext']>,
) => useStore.getState().setGameWindowContext(payload);

export const resetGameWindowStore = () =>
  useStore.getState().resetGameWindowContext();

export const setGeolocationContext = (payload: GeolocationContextPayload) =>
  useStore.getState().setGeolocationContext(payload);

export const setGeolocationStatus = (payload: GeolocationStatusPayload) =>
  useStore.getState().setGeolocationStatus(payload);

export const setGeolocationPassed = (geolocationToken: string) =>
  useStore.getState().setGeolocationPassed(geolocationToken);

export const setGeolocationError = (payload: GeolocationErrorPayload) =>
  useStore.getState().setGeolocationError(payload);

export const setPreChecksComplete = (complete: boolean) =>
  useStore.getState().setPreChecksComplete(complete);

export const incrementGeolocationRetryCount = () =>
  useStore.getState().incrementRetryCount();

export const resetGeolocationStore = (part?: GeolocationStoreParts) => {
  if (part) {
    useStore.getState().resetGeolocationPart(part);
  }
  useStore.getState().resetAllGeolocationParts();
};

export const resetGeolocationWithTriggerUserLocate = () =>
  useStore.getState().resetGeolocationForRelocate();

export const setCurrentTime = (time: TimerSlice['currentTime']) =>
  useStore.getState().setCurrentTime(time);
export const setSessionTime = (time: TimerSlice['sessionTime']) =>
  useStore.getState().setSessionTime(time);
export const setGameTime = (time: TimerSlice['gameTime']) =>
  useStore.getState().setGameTime(time);
export const setServerTimeValue = (val: number) =>
  useStore.getState().setServerTimeValue(val);
export const setSessionStartValue = (val: number) =>
  useStore.getState().setSessionStartValue(val);
export const setGameStartValue = (val: number) =>
  useStore.getState().setGameStartValue(val);
export const resetGameTime = () => useStore.getState().resetGameTime();

export const addTheme = (name: ThemeSlice['themes'][number] | string) =>
  useStore.getState().addTheme(name as string);
export const changeTheme = (name: ThemeSlice['theme'] | string) =>
  useStore.getState().changeTheme(name as string);
export const resetTheme = () => useStore.getState().resetTheme();

export const setCustomerData = (payload: CustomerDataPayload) =>
  useStore.getState().setCustomerData(payload);

export const setCustomerPreferences = (payload: CustomerPreferencesPayload) =>
  useStore.getState().setCustomerPreferences(payload);

export const setAmplitudeExperiments = (payload: AmplitudeExperimentsPayload) =>
  useStore.getState().setAmplitudeExperiments(payload);

export const setCustomerDataError = (payload: CustomerDataErrorPayload) =>
  useStore.getState().setCustomerDataError(payload);

export const resetCustomerDataStore = (part?: CustomerStoreParts) => {
  if (part) {
    return useStore.getState().resetCustomerStorePart(part);
  }
  return useStore.getState().resetAllCustomerParts();
};

export const addToQueue = (payload: { type: PopupTypes; popup: () => void }) =>
  useStore.getState().addToQueue(payload);
export const removeFromQueue = () => useStore.getState().removeFromQueue();
export const removeFromQueueSpecific = (payload: { type: PopupTypes }) =>
  useStore.getState().removeFromQueueSpecific(payload);
export const executeQueue = () => {
  const { state, isPresenting, executeQueue: runQueue } = useStore.getState();
  if (!state?.length || isPresenting) return;
  return runQueue();
};
export const resetQueueState = () => useStore.getState().resetQueueState();

export const setRecentlyPlayedContext = (
  payload: RecentlyPlayedContextPayload,
) => useStore.getState().setRecentlyPlayedContext(payload);

export const setRecentlyPlayedStatus = (payload: RecentlyPlayedStatusPayload) =>
  useStore.getState().setRecentlyPlayedStatus(payload);

export const setRecentlyPlayedError = (payload: RecentlyPlayedErrorPayload) =>
  useStore.getState().setRecentlyPlayedError(payload);

export const resetRecentlyPlayedStore = (part: RecentlyPlayedStoreParts) => {
  if (part) {
    return useStore.getState().resetRecentlyPlayedPart(part);
  }
  return useStore.getState().resetAllRecentlyPlayedParts();
};

export const setUserContext = (payload: UserContextPayload) =>
  useStore.getState().setUserContext(payload);

export const setUserContextStatus = (payload: UserStatusPayload) =>
  useStore.getState().setUserContextStatus(payload);

export const setUserContextError = (payload: UserErrorPayload) =>
  useStore.getState().setUserContextError(payload);

export const resetUserContextStore = (
  part: import('./slices/userContext/types').UserStoreParts,
) => {
  if (part) {
    return useStore.getState().resetUserContextPart(part);
  }
  useStore.getState().resetAllUserContextParts();
};

// Net position UI lock
export const setLockUI = (lockUI: boolean) =>
  useStore.getState().setLockUI(lockUI);

// Showoff actions
export const setShowoff = (payload: Partial<ShowoffSlice['showoffContext']>) =>
  useStore.getState().setShowoffContext(payload);

export const resetShowoffStore = () =>
  useStore.getState().resetShowoffContext();

/** Named group of slice error setters (for targeted imports without the `setError` router). */
export const sliceErrorSetters = {
  setCustomerDataError,
  setCommonUIError,
  setGeolocationError,
  setUserContextError,
  setRecentlyPlayedError,
  setGameError,
  setUserSessionError,
};

/** Dispatches slice-local errors by domain (used by `handleError` and legacy call sites). */
export const setError = (type: ErrorType, payload?: ErrorPayload) => {
  const details = payload
    ? {
        message: payload.message ?? null,
        reason: payload.reason ?? null,
      }
    : { message: null, reason: null };

  const store = useStore.getState();
  switch (type) {
    case ErrorType.SESSION:
      store.setUserSessionError({
        type: ErrorType.SESSION,
        details,
        ...(payload?.service != null
          ? { service: String(payload.service) }
          : {}),
      });
      break;
    case ErrorType.API:
      store.setCustomerDataError({
        type: ErrorType.API,
        service: payload?.service != null ? String(payload.service) : null,
        details,
      });
      break;
    case ErrorType.CLIENT:
      store.setGameError({
        type: ErrorType.CLIENT,
        details,
      });
      break;
    case ErrorType.PARSE:
      store.setUserContextError({
        type: ErrorType.PARSE,
        category: 'parse',
        details,
      });
      break;
    case ErrorType.GEOLOCATION:
      store.setGeolocationError({
        type: payload?.type ?? ErrorType.GEOLOCATION,
        details,
      });
      break;
    case ErrorType.GAME:
      store.setGameError({
        type: payload?.errorType as string,
        errorCode: payload?.errorCode,
        details,
      });
      break;
    case ErrorType.DEFAULT:
    default:
      store.setUserContextError({
        type: ErrorType.DEFAULT,
        category: 'default',
        details,
      });
      break;
  }
};

export const setJackpotData = (
  data: NonNullable<SwjSliceProps['swj']['jackpotData']>,
) => useStore.getState().swj.setJackpotData(data);

export const setIsDybFeatureFlagEnabled = (dybFlag: boolean) =>
  useStore.getState().swj.setIsDybFeatureFlagEnabled(dybFlag);

export const setIsSwjFlagEnabled = (swjFlag: boolean) =>
  useStore.getState().swj.setIsSwjFlagEnabled(swjFlag);

export const setIsJackpotModeSuppressed = (suppressed: boolean) =>
  useStore.getState().swj.setIsJackpotModeSuppressed(suppressed);

export const setDeviceOrientationInfo = (info: DeviceOrientationInfo) =>
  useStore.getState().deviceSafeZone.setDeviceOrientationInfo(info);
